import React, { useEffect, useState } from 'react';
import axios from 'axios';
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";

const PrivacyPolicy = () => {
    const [policyData, setPolicyData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPrivacyPolicy = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/privacy-policy/latest/`);
                setPolicyData(response.data);
                setIsLoading(false);
            } catch (err) {
                console.error('Error fetching privacy policy:', err);
                setError(err.message);
                setIsLoading(false);
            }
        };

        fetchPrivacyPolicy();
    }, []);

    // Helper function to convert text with newlines to bullet list
    const renderList = (text) => {
        if (!text) return null;
        return text.split('\n').filter(item => item.trim()).map((item, index) => (
            <li key={index}>{item.trim()}</li>
        ));
    };

    if (isLoading) {
        return (
            <>
                <HeaderOne />
                <div className="privacy-container">
                    <div className="text-center py-5">Loading Privacy Policy...</div>
                </div>
                <BottomFooter />
            </>
        );
    }

    if (error || !policyData) {
        return (
            <>
                <HeaderOne />
                <div className="privacy-container">
                    <div className="text-center py-5 text-danger">
                        Error loading privacy policy. Please try again later.
                    </div>
                </div>
                <BottomFooter />
            </>
        );
    }

    return (
        <>
            {/* Header */}
            <HeaderOne />

            <div className="privacy-container">
                {/* Hero Section */}
                <section className="privacy-hero-section">
                    <div className="hero-content">
                        <h1 className="hero-title">{policyData.page_title}</h1>
                        <p className="hero-subtitle">Effective date: {policyData.effective_date}</p>
                    </div>
                </section>

                {/* Main Content */}
                <section className="privacy-main-content">
                    <div className="content-wrapper">
                        
                        {/* Information Collection */}
                        <div className="policy-section">
                            <h2>{policyData.info_collection_title}</h2>
                            <p>{policyData.info_collection_text}</p>

                            <h3>{policyData.types_of_data_title}</h3>

                            <h4>{policyData.personal_data_title}</h4>
                            <p>{policyData.personal_data_text}</p>
                            {policyData.personal_data_list && (
                                <ul>{renderList(policyData.personal_data_list)}</ul>
                            )}

                            <h4>{policyData.usage_data_title}</h4>
                            <p>{policyData.usage_data_text}</p>

                            <h4>{policyData.tracking_cookies_title}</h4>
                            <p>{policyData.tracking_cookies_text1}</p>
                            <p>{policyData.tracking_cookies_text2}</p>
                            <p>{policyData.tracking_cookies_text3}</p>
                            {policyData.tracking_cookies_examples && (
                                <ul>{renderList(policyData.tracking_cookies_examples)}</ul>
                            )}
                        </div>

                        {/* Use of Data */}
                        <div className="policy-section">
                            <h2>{policyData.use_of_data_title}</h2>
                            <p>Panda Print uses the collected data for various purposes:</p>
                            {policyData.use_of_data_list && (
                                <ul>{renderList(policyData.use_of_data_list)}</ul>
                            )}
                        </div>

                        {/* Transfer of Data */}
                        <div className="policy-section">
                            <h2>{policyData.transfer_data_title}</h2>
                            <p>{policyData.transfer_data_text1}</p>
                            <p>{policyData.transfer_data_text2}</p>
                            <p>{policyData.transfer_data_text3}</p>
                            <p>{policyData.transfer_data_text4}</p>
                        </div>

                        {/* Disclosure of Data */}
                        <div className="policy-section">
                            <h2>{policyData.disclosure_title}</h2>
                            <h3>{policyData.disclosure_subtitle}</h3>
                            <p>Panda Print may disclose your Personal Data in the good faith belief that such action is necessary to:</p>
                            {policyData.disclosure_list && (
                                <ul>{renderList(policyData.disclosure_list)}</ul>
                            )}
                        </div>

                        {/* Security of Data */}
                        <div className="policy-section">
                            <h2>{policyData.security_title}</h2>
                            <p>{policyData.security_text}</p>
                        </div>

                        {/* Service Providers */}
                        <div className="policy-section">
                            <h2>{policyData.service_providers_title}</h2>
                            <p>{policyData.service_providers_text1}</p>
                            <p>{policyData.service_providers_text2}</p>

                            <h3>{policyData.analytics_title}</h3>
                            <p>We may use third-party Service Providers to monitor and analyze the use of our Service.</p>
                            <p><strong>{policyData.google_analytics_title}</strong></p>
                            <p>{policyData.google_analytics_text1}</p>
                            <p>{policyData.google_analytics_text2}</p>
                            <p>{policyData.google_analytics_text3}</p>
                        </div>

                        {/* Links to Other Sites */}
                        <div className="policy-section">
                            <h2>{policyData.links_title}</h2>
                            <p>{policyData.links_text1}</p>
                            <p>{policyData.links_text2}</p>
                        </div>

                        {/* Litespeed Cache */}
                        <div className="policy-section">
                            <h2>{policyData.litespeed_title}</h2>
                            <p>{policyData.litespeed_text1}</p>
                            <p>{policyData.litespeed_text2}</p>
                        </div>

                        {/* MailPoet */}
                        <div className="policy-section">
                            <h2>{policyData.mailpoet_title}</h2>
                            <p>{policyData.mailpoet_text1}</p>
                            <p>{policyData.mailpoet_text2}</p>
                            <p>{policyData.mailpoet_text3}</p>
                            <p>{policyData.mailpoet_text4}</p>
                            <p>{policyData.mailpoet_text5}</p>
                        </div>

                        {/* Akismet */}
                        <div className="policy-section">
                            <h2>{policyData.akismet_title}</h2>
                            <p>{policyData.akismet_text}</p>
                        </div>

                        {/* Children's Privacy */}
                        <div className="policy-section">
                            <h2>{policyData.children_title}</h2>
                            <p>{policyData.children_text1}</p>
                            <p>{policyData.children_text2}</p>
                        </div>

                        {/* Changes to Privacy Policy */}
                        <div className="policy-section">
                            <h2>{policyData.changes_title}</h2>
                            <p>{policyData.changes_text1}</p>
                            <p>{policyData.changes_text2}</p>
                            <p>{policyData.changes_text3}</p>
                        </div>
                    </div>
                </section>
            </div>

            {/* Bottom Footer */}
            <BottomFooter />
        </>
    );
};

export default PrivacyPolicy;
